import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  let s = document.getElementById('razorpay-checkout-js');
  if (!s) {
    s = document.createElement('script');
    s.id = 'razorpay-checkout-js';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
  }
  s.addEventListener('load', () => resolve(true));
  s.addEventListener('error', () => resolve(false));
});

const reportFailure = (payload) => axios.post(`${API}/billing/payments/failed`, payload, { headers: auth() }).catch(() => {});

const failToast = (msg, retry) => toast.error(msg, {
  duration: 10000,
  action: retry ? { label: 'Retry', onClick: retry } : undefined,
});

// One-time AI credit pack purchase. Credits are added only after server-side signature verification.
export async function buyCreditPack(bundle, { onSuccess } = {}) {
  const retry = () => buyCreditPack(bundle, { onSuccess });
  try {
    const { data } = await axios.post(`${API}/billing/topup/create-order`, { bundle_code: bundle.code }, { headers: auth() });
    if (!(await loadRazorpay())) { failToast('Payment checkout could not load. Check your connection.', retry); return; }
    const rzp = new window.Razorpay({
      key: data.key_id,
      order_id: data.order_id,
      amount: Math.round(data.amount * 100),
      currency: 'INR',
      name: 'RAW — AI Credits',
      description: `${bundle.pack_name || bundle.name} · ${Number(bundle.credits).toLocaleString('en-IN')} credits`,
      theme: { color: '#0f766e' },
      handler: async (rsp) => {
        try {
          const v = await axios.post(`${API}/billing/topup/verify`, { ...rsp, bundle_code: bundle.code }, { headers: auth() });
          toast.success(`Credits added successfully. Your wallet now has ${Number(v.data?.balance || 0).toLocaleString('en-IN')} credits.`);
          window.dispatchEvent(new Event('bc:credits-changed'));
          onSuccess?.(v.data);
        } catch (e) {
          failToast(e.response?.data?.detail || 'Payment verification failed — no credits were added.', retry);
        }
      },
      modal: { ondismiss: () => toast('Checkout closed — no payment was taken.') },
    });
    rzp.on('payment.failed', (resp) => {
      reportFailure({ kind: 'topup', razorpay_order_id: data.order_id, razorpay_payment_id: resp?.error?.metadata?.payment_id, reason: resp?.error?.description });
      failToast(`Payment failed: ${resp?.error?.description || 'please try again'}. No credits were added.`, retry);
    });
    rzp.open();
  } catch (e) {
    failToast(e.response?.data?.detail || 'Could not start checkout', retry);
  }
}

// Recurring plan subscription. The plan activates only after the server verifies the first payment.
export async function startSubscription(plan, billingPeriod, { onSuccess, onDone } = {}) {
  const retry = () => startSubscription(plan, billingPeriod, { onSuccess, onDone });
  try {
    const { data } = await axios.post(`${API}/billing/subscribe`, { plan_code: plan.code, billing_period: billingPeriod }, { headers: auth() });
    if (data.free_activated) { onSuccess?.({ plan_code: data.plan_code, plan_name: plan.name, free: true }); return; }
    if (!(await loadRazorpay())) { failToast('Payment checkout could not load. Check your connection.', retry); return; }
    const rzp = new window.Razorpay({
      key: data.razorpay_key_id,
      subscription_id: data.subscription_id,
      name: 'RAW',
      description: `${plan.name} · ₹${Number(data.amount || plan.amount || 0).toLocaleString('en-IN')}/${billingPeriod === 'yearly' ? 'yr' : 'mo'}`,
      theme: { color: '#0f766e' },
      handler: async (rsp) => {
        try {
          const v = await axios.post(`${API}/billing/subscribe/verify`, rsp, { headers: auth() });
          window.dispatchEvent(new Event('bc:credits-changed'));
          onSuccess?.(v.data);
        } catch (e) {
          failToast(e.response?.data?.detail || 'Payment verification failed — your plan was not changed.', retry);
        }
      },
      modal: { ondismiss: () => toast('Checkout closed — your plan was not changed.') },
    });
    rzp.on('payment.failed', (resp) => {
      reportFailure({ kind: 'subscription', razorpay_subscription_id: data.subscription_id, razorpay_payment_id: resp?.error?.metadata?.payment_id, reason: resp?.error?.description });
      failToast(`Payment failed: ${resp?.error?.description || 'please try again'}. Your plan was not activated.`, retry);
    });
    rzp.open();
  } catch (e) {
    failToast(e.response?.data?.detail || 'Could not start checkout', retry);
  } finally {
    onDone?.();
  }
}
